import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function GoogleLoginButton({ label }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { setUser } = useAuth();

  const handleClick = useCallback(async () => {
    // Detect Capacitor native platform
    const { Capacitor: Cap } = await import("@capacitor/core");
    const isNative = Cap.isNativePlatform?.();

    if (isNative) {
      const { Browser } = await import("@capacitor/browser");
      const backendOrigin = process.env.REACT_APP_BACKEND_URL.replace(/\/_\/backend$/, "");
      const redirectUrl = `${backendOrigin}/auth/callback`;
      try {
        const result = await Browser.openWebAuth({
          url: `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`,
          redirectUrl: redirectUrl,
        });
        const hash = result.url.split("#")[1] || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
          toast.error(t("auth.missingSession"));
          return;
        }
        const { data } = await api.post("/auth/google/session", { session_id: m[1] });
        localStorage.setItem("hg_token", data.token);
        setUser(data.user);
        toast.success(t("auth.welcomeBack") + ", " + data.user.name + "!");
        const dest = data.user.role === "admin" ? "/admin" : data.user.role === "worker" ? "/worker" : "/dashboard";
        nav(dest, { replace: true });
      } catch {
        toast.error(t("auth.googleSignInFailed"));
      }
    } else {
      // Web: redirect-based flow
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  }, [t, nav, setUser]);

  return (
    <Button
      type="button"
      onClick={handleClick}
      data-testid="google-login-btn"
      variant="outline"
      className="w-full rounded-sm border-[#E2E0D9] bg-white hover:bg-[#F3F1ED] text-[#1A1D1A] py-6 flex items-center gap-3 justify-center"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a10.99 10.99 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
      </svg>
      {label || t("auth.continueWithGoogle")}
    </Button>
  );
}
